import {useEffect, useRef, useState} from 'react';
import {audioPeaks, decodeMediaAudio} from '../lib/mediaAudio';

export function useAudioPeaks(url) {
  const [state, setState] = useState({url: null, status: 'idle'});
  useEffect(() => {
    if (!url) return;
    let active = true;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) { setState({url, status: 'unavailable'}); return; }
    const context = new AudioContext();
    setState({url, status: 'loading'});
    decodeMediaAudio(url, context).then(buffer => {
      if (active) setState({url, status: 'ready', ...audioPeaks(buffer)});
    }).catch(() => { if (active) setState({url, status: 'unavailable'}); })
      .finally(() => context.close().catch(() => {}));
    return () => { active = false; };
  }, [url]);
  return state.url === url ? state : {status: url ? 'loading' : 'idle'};
}

export default function AudioWaveform({audio, start, end}) {
  const canvas = useRef(null);
  useEffect(() => {
    const node = canvas.current;
    if (!node || !audio.peaks) return;
    const draw = () => {
      const width = node.clientWidth, height = node.clientHeight;
      if (!width || !height) return;
      const ratio = window.devicePixelRatio || 1;
      node.width = Math.ceil(width * ratio); node.height = Math.ceil(height * ratio);
      const ctx = node.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.fillStyle = getComputedStyle(node).getPropertyValue('--waveform-ink').trim() || '#9eb7aa';
      const origin = audio.offset || 0;
      for (let x = 0; x < width; x++) {
        const from = Math.max(0, Math.floor((start + x / width * (end - start) - origin) * audio.rate));
        const to = Math.min(audio.peaks.length, Math.ceil((start + (x + 1) / width * (end - start) - origin) * audio.rate));
        let peak = 0;
        for (let i = from; i < to; i++) peak = Math.max(peak, audio.peaks[i]);
        const amplitude = peak * (height - 4);
        ctx.fillRect(x, (height - amplitude) / 2, 1, Math.max(.5, amplitude));
      }
    };
    const observer = new ResizeObserver(draw);
    observer.observe(node); draw();
    const themeObserver = new MutationObserver(draw);
    themeObserver.observe(document.documentElement, {attributes:true, attributeFilter:['data-theme']});
    return () => {observer.disconnect();themeObserver.disconnect();};
  }, [audio, start, end]);
  return <canvas ref={canvas} className="audio-waveform" role="img" aria-label="Audio waveform for retained footage" />;
}
